import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Copy, PlusCircle, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const ContextMenu = ({ x, y, items, onClose, triggerElement }) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const updatePosition = () => {
      if (triggerElement && triggerElement.current) {
        const rect = triggerElement.current.getBoundingClientRect();
        // Keep the menu relative to the trigger element
        setPosition({ x: rect.right + 8, y: rect.top });
      }
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, triggerElement]);

  return createPortal(
    <div
      ref={menuRef}
      className="fixed bg-white dark:bg-gray-800 shadow-md rounded-md p-2 z-50"
      style={{
        top: `${position.y}px`,
        left: `${position.x}px`,
      }}
    >
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li
            key={index}
            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded p-1 transition-colors duration-300"
            onClick={() => {
              item.action();
              onClose();
            }}
          >
            {React.cloneElement(item.icon, { className: "w-4 h-4" })}
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>,
    document.body,
  );
};

const ColorHistory = ({ colors = [], onColorClick, addColorToCustomPalette, clearColorHistory }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedColor, setSelectedColor] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [triggerElement, setTriggerElement] = useState(null);
  const scrollContainerRef = useRef(null);

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleContextMenu = useCallback((e, color) => {
    e.preventDefault();
    setSelectedColor(color);
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setTriggerElement({ current: e.currentTarget });
    setShowMenu(true);
  }, []);

  const menuItems = useCallback(() => {
    if (!selectedColor) return [];

    return [
      {
        icon: <Copy />,
        label: "Copy HEX",
        action: () => copyToClipboard(selectedColor),
      },
      ...(typeof addColorToCustomPalette === "function" ? [{
        icon: <PlusCircle />,
        label: "Add to custom palette",
        action: () => addColorToCustomPalette(selectedColor),
      }] : [])
    ];
  }, [selectedColor, copyToClipboard, addColorToCustomPalette]);

  // Check scroll position to show/hide arrows
  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Check scroll on mount and when colors change
  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [colors, checkScroll]);

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };
  return (
    <div data-color-history className="mt-6">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-md font-medium">Your Color History</h3>
        {colors.length > 0 && (
          <>
            <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full font-medium">
              {colors.length} color{colors.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={clearColorHistory}
              className="ml-auto text-xs px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md font-medium transition-colors duration-200 flex items-center gap-1.5"
              title="Clear all color history"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear All
            </button>
          </>
        )}
      </div>

      {colors.length === 0 ? (
        // Compact empty state
        <div className="flex items-center justify-center py-4 px-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-200 dark:border-gray-600">
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-2 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 110 2h-1v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 110-2h4zM9 6v10h6V6H9z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              No colors to display. Play with some first
            </p>
          </div>
        </div>
      ) : (
        // Enhanced color display when colors are present
        <div className="relative">
          {/* Background gradient for visual interest */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 dark:from-purple-900/10 dark:via-blue-900/10 dark:to-indigo-900/10 rounded-xl -z-10"></div>

          {/* Main container */}
          <div className="relative bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl p-4 shadow-sm">
            {/* Scroll container with arrows */}
            <div className="relative group">
              {/* Left arrow */}
              {showLeftArrow && (
                <button
                  onClick={scrollLeft}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 opacity-0 group-hover:opacity-100"
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              )}

              {/* Right arrow */}
              {showRightArrow && (
                <button
                  onClick={scrollRight}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 opacity-0 group-hover:opacity-100"
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              )}

              {/* Scrollable color container */}
              <div
                ref={scrollContainerRef}
                onScroll={checkScroll}
                className="flex gap-3 overflow-x-auto scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {colors.map((color, index) => (
                  <div key={index} className="group/item relative flex-shrink-0">
                    {/* Color button */}
                    <button
                      className="relative w-10 h-10 rounded-xl border-2 border-white dark:border-gray-700 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all duration-200 hover:scale-110 hover:shadow-lg group-hover/item:border-gray-300 dark:group-hover/item:border-gray-500"
                      style={{ backgroundColor: color }}
                      onClick={() => onColorClick(color)}
                      onContextMenu={(e) => handleContextMenu(e, color)}
                      title={color}
                    >
                      {/* Subtle inner shadow for depth */}
                      <div className="absolute inset-1 rounded-lg shadow-inner opacity-20 pointer-events-none"></div>
                    </button>

                    {/* Color value tooltip on hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover/item:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                      <div className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                        {color}
                        {/* Arrow */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-white rotate-45 -mt-1"></div>
                      </div>
                    </div>

                    {/* Position indicator for recent colors */}
                    {index === 0 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm">
                        <div className="absolute inset-0.5 bg-green-400 rounded-full animate-pulse"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom gradient line for visual appeal */}
            <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-blue-200 dark:via-blue-700 to-transparent"></div>
          </div>

          {/* Subtle corner accent */}
          <div className="absolute top-2 right-2 w-2 h-2 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-30"></div>
        </div>
      )}
      {showMenu && (
        <ContextMenu
          x={menuPosition.x}
          y={menuPosition.y}
          items={menuItems()}
          onClose={() => setShowMenu(false)}
          triggerElement={triggerElement}
        />
      )}
    </div>
  );
};

export default ColorHistory;